/**
 * Correo de la empresa — una sola tarjeta para todos los proveedores.
 *
 * REEMPLAZA a dos tarjetas que hacían lo mismo por caminos distintos
 * (SendGridConfigCard y MailjetConfigCard) y que guardaban en lugares
 * distintos. Medido antes de retirarlas: CERO empresas las habían usado, así
 * que no se pierde configuración de ningún cliente.
 *
 * SE DIBUJA SOLA. Los proveedores y sus campos vienen del backend, así que
 * agregar uno nuevo no obliga a tocar este archivo.
 *
 * TRES COSAS QUE HACE DISTINTO
 *  - La credencial nunca llega al navegador: se muestra cuántos caracteres
 *    tiene y en qué termina. Ni siquiera enmascarada, porque enmascarada
 *    significa que viajó.
 *  - Dejar el campo vacío NO borra la llave guardada. Eso fue lo que dejó a
 *    Casas KREA sin IA durante horas.
 *  - Antes de guardar se le pregunta al proveedor si la credencial sirve.
 */
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import HelpTip from '@/components/HelpTip';
import { Mail, Loader2, CheckCircle2, AlertTriangle, Save, Plug, Globe } from 'lucide-react';

const ETIQUETAS = {
  api_key: 'API Key',
  api_secret: 'Secret Key',
  remitente_email: 'Correo del remitente',
  remitente_nombre: 'Nombre del remitente',
};

const AYUDAS = {
  remitente_email:
    'La dirección desde la que salen tus correos. Tiene que ser de un dominio que ya hayas '
    + 'verificado con tu proveedor: si mandas desde un dominio sin verificar, el correo se va a '
    + 'spam o ni sale.',
  remitente_nombre:
    'El nombre que ve el cliente en su bandeja, antes de abrir. Usa el de tu negocio, no el de '
    + 'una persona: "Casas KREA" abre más que "ventas1".',
};

const esSecreto = (campo) => campo === 'api_key' || campo === 'api_secret';

function ProveedorCorreo() {
  const [proveedores, setProveedores] = useState([]);
  const [config, setConfig] = useState(null);
  const [elegido, setElegido] = useState('');
  const [valores, setValores] = useState({});
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [probando, setProbando] = useState(false);

  const cargar = () => {
    setCargando(true);
    Promise.all([
      api.get('/correo/proveedores'),
      api.get('/correo/config').catch(() => ({ data: { configurado: false } })),
    ]).then(([p, c]) => {
      setProveedores(p.data?.proveedores || []);
      setConfig(c.data);
      if (c.data?.proveedor) {
        setElegido(c.data.proveedor);
        const previos = {};
        Object.entries(c.data.credenciales || {}).forEach(([k, v]) => {
          if (!esSecreto(k)) previos[k] = v;
        });
        setValores(previos);
      }
    }).catch(() => toast.error('No se pudo cargar la configuración de correo'))
      .finally(() => setCargando(false));
  };
  useEffect(cargar, []); // eslint-disable-line

  const definicion = proveedores.find(p => p.clave === elegido);
  const yaConfigurado = config?.configurado && config?.proveedor === elegido;

  const guardar = () => {
    if (!elegido) { toast.error('Elige un proveedor'); return; }
    setGuardando(true);
    api.put('/correo/config', { proveedor: elegido, credenciales: valores })
      .then(r => {
        toast.success(r.data?.mensaje || 'Guardado');
        setValores(v => ({ ...v, api_key: '', api_secret: '' }));
        cargar();
      })
      .catch(e => toast.error(e?.response?.data?.detail || 'No se pudo guardar'))
      .finally(() => setGuardando(false));
  };

  const probar = () => {
    setProbando(true);
    api.post('/correo/config/probar')
      .then(r => (r.data?.ok ? toast.success(r.data.mensaje) : toast.error(r.data?.mensaje || 'No sirve')))
      .catch(e => toast.error(e?.response?.data?.detail || 'No se pudo probar'))
      .finally(() => { setProbando(false); cargar(); });
  };

  const estado = config?.configurado && config?.activo;

  return (
    <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="correo-config-card">
      <CardHeader className="pb-4">
        <CardTitle className="font-heading text-base text-white flex items-center gap-2">
          <Mail className="w-4 h-4 text-sky-400" />Correo de tu empresa
          <HelpTip
            title="Para qué sirve"
            content={
              'Aquí conectas tu propia cuenta de correo para que los envíos salgan CON TU DOMINIO '
              + 'y se cobren a tu cuenta, no a la de la plataforma.\n\n'
              + 'Es lo que usan los correos de marketing, el aviso de nuevo lead y la confirmación '
              + 'de cita.\n\n'
              + 'Si no lo configuras, esos envíos no salen. Preferimos eso a mandarlos con un '
              + 'dominio que no es tuyo: cuando alguien marca como spam un correo, la reputación '
              + 'que se quema es la del dominio que firma.'
            }
          />
          {estado
            ? <Badge variant="outline" className="text-[10px] border-emerald-500/30 text-emerald-400">conectado</Badge>
            : <Badge variant="outline" className="text-[10px] border-white/10 text-muted-foreground">sin conectar</Badge>}
        </CardTitle>
        <CardDescription className="text-muted-foreground text-xs">
          Elige tu proveedor y pega tus llaves. Antes de guardar le preguntamos si sirven.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {cargando ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label className="text-xs">Proveedor</Label>
                <HelpTip
                  title="Cuál elijo"
                  content={
                    'Los tres envían igual de bien. Elige el que ya uses o el que te acomode:\n\n'
                    + 'SendGrid: el más común, buena documentación.\n'
                    + 'Brevo: suele salir más barato por volumen.\n'
                    + 'Mailjet: pide DOS llaves en vez de una.\n\n'
                    + 'Solo aparecen proveedores que envían de verdad. Si no ves el tuyo, dinos y '
                    + 'lo agregamos.'
                  }
                />
              </div>
              <Select value={elegido} onValueChange={(v) => { setElegido(v); setValores({}); }}>
                <SelectTrigger className="bg-black/20 border-white/10" data-testid="correo-proveedor">
                  <SelectValue placeholder="Elige tu proveedor de correo" />
                </SelectTrigger>
                <SelectContent>
                  {proveedores.map(p => (
                    <SelectItem key={p.clave} value={p.clave}>{p.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {definicion?.ayuda_llave && (
                <p className="text-[11px] text-muted-foreground">{definicion.ayuda_llave}</p>
              )}
            </div>

            {definicion && (
              <div className="space-y-3">
                {definicion.campos.map(campo => {
                  const previo = (config?.credenciales || {})[campo];
                  const yaHay = esSecreto(campo) && previo?.configurada && yaConfigurado;
                  return (
                    <div key={campo} className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          <Label className="text-xs">{ETIQUETAS[campo] || campo}</Label>
                          {AYUDAS[campo] && <HelpTip title={ETIQUETAS[campo]} content={AYUDAS[campo]} />}
                        </div>
                        {yaHay && (
                          <span className="text-[10px] text-emerald-400 font-mono">
                            guardada · {previo.longitud} caracteres · termina en {previo.final || '—'}
                          </span>
                        )}
                      </div>
                      <Input
                        type={esSecreto(campo) ? 'password' : 'text'}
                        autoComplete="new-password"
                        value={valores[campo] || ''}
                        onChange={e => setValores({ ...valores, [campo]: e.target.value })}
                        placeholder={
                          yaHay
                            ? 'Dejar vacío = no se toca. Escribir = la reemplaza.'
                            : (esSecreto(campo) ? 'Pega aquí tu llave' : '')
                        }
                        className="bg-black/20 border-white/10 font-mono text-xs"
                        data-testid={`correo-campo-${campo}`}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {config?.configurado && (
              <div className="p-3 rounded-lg bg-black/20 border border-white/5 flex items-start gap-2">
                {config.ultima_prueba_ok === false
                  ? <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                  : <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />}
                <p className="text-[11px] text-muted-foreground">
                  {config.ultima_prueba_ok === false
                    ? 'La última prueba falló. Tus correos no están saliendo: revisa la llave.'
                    : 'Credencial verificada con el proveedor.'}
                  {config.verificado_en && ` (${String(config.verificado_en).slice(0, 10)})`}
                </p>
              </div>
            )}

            <div className="flex items-center justify-between pt-1">
              <Button
                variant="outline" size="sm" onClick={probar}
                disabled={probando || !config?.configurado}
                className="border-white/10 text-xs" data-testid="correo-probar"
              >
                {probando ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Plug className="w-3 h-3 mr-1" />}
                Probar la que ya está guardada
              </Button>
              <Button
                onClick={guardar} disabled={guardando || !elegido}
                className="bg-sky-500 text-white hover:bg-sky-600" data-testid="correo-guardar"
              >
                {guardando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Guardar y verificar
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * El dominio de envío. Sin esto, el correo llega a spam o no llega:
 * Gmail y Outlook exigen dominio autenticado a quien manda volumen.
 *
 * La verdad la dice el PROVEEDOR, nunca el DNS. Se aprendió en CavernicolApp:
 * de las tres llaves que genera, a veces solo se publica una y el proveedor da
 * el dominio por bueno igual. Resolverlo por nuestra cuenta daría un
 * "pendiente" eterno y mandaría al cliente a recapturar registros correctos.
 */
function DominioEnvio() {
  const [estado, setEstado] = useState(null);
  const [dominio, setDominio] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [copiado, setCopiado] = useState('');

  const cargar = () => {
    api.get('/correo/dominio')
      .then(r => { setEstado(r.data); if (r.data?.dominio) setDominio(r.data.dominio); })
      .catch(() => {});
  };
  useEffect(cargar, []); // eslint-disable-line

  const darDeAlta = () => {
    setOcupado(true);
    api.post('/correo/dominio', { dominio })
      .then(r => {
        toast.success('Dominio dado de alta. Ahora captura los registros.');
        if (r.data?.cloudflare?.automatico) {
          toast.success(`Pusimos los ${r.data.cloudflare.puestos} registros por ti.`);
        }
        cargar();
      })
      .catch(e => toast.error(e?.response?.data?.detail || 'No se pudo dar de alta'))
      .finally(() => setOcupado(false));
  };

  const verificar = () => {
    setOcupado(true);
    api.post('/correo/dominio/verificar')
      .then(r => (r.data?.estado === 'verificado' ? toast.success(r.data.mensaje) : toast.info(r.data.mensaje)))
      .catch(e => toast.error(e?.response?.data?.detail || 'No se pudo verificar'))
      .finally(() => { setOcupado(false); cargar(); });
  };

  const copiar = (texto, id) => {
    navigator.clipboard?.writeText(texto);
    setCopiado(id);
    setTimeout(() => setCopiado(''), 1500);
  };

  const listo = estado?.estado === 'verificado';

  return (
    <Card className="bg-card/50 backdrop-blur-md border-white/5 mt-4" data-testid="correo-dominio-card">
      <CardHeader className="pb-4">
        <CardTitle className="font-heading text-base text-white flex items-center gap-2">
          <Globe className="w-4 h-4 text-violet-400" />Tu dominio de envío
          <HelpTip
            title="Por qué es obligatorio"
            content={
              'Es lo que le demuestra a Gmail y a Outlook que los correos salen de verdad de tu '
              + 'empresa y no de alguien haciéndose pasar por ti.\n\n'
              + 'Sin esto, tus correos llegan a spam o no llegan. Desde 2024 los dos lo exigen a '
              + 'quien manda volumen.\n\n'
              + 'Son tres renglones que hay que capturar UNA vez donde vive tu dominio (el mismo '
              + 'lugar donde apuntaste tu página web). Si tu dominio lo administramos nosotros, '
              + 'los ponemos por ti.'
            }
          />
          {listo
            ? <Badge variant="outline" className="text-[10px] border-emerald-500/30 text-emerald-400">verificado</Badge>
            : estado?.configurado
              ? <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-400">pendiente</Badge>
              : null}
        </CardTitle>
        <CardDescription className="text-muted-foreground text-xs">
          Puedes escribir tu correo, la dirección de tu página o solo el dominio: nosotros lo entendemos.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-1.5">
            <Label className="text-xs">Dominio</Label>
            <Input
              value={dominio} onChange={e => setDominio(e.target.value)}
              placeholder="casaskrea.com" className="bg-black/20 border-white/10"
              data-testid="correo-dominio-input"
            />
          </div>
          <Button onClick={darDeAlta} disabled={ocupado || !dominio.trim()}
            className="bg-violet-500 text-white hover:bg-violet-600" data-testid="correo-dominio-alta">
            {ocupado ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Dar de alta'}
          </Button>
        </div>

        {estado?.configurado && (estado.registros || []).length > 0 && (
          <div className="space-y-2">
            {estado.cloudflare?.automatico ? (
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <p className="text-[11px] text-emerald-200/90">
                  Tu dominio lo administramos nosotros, así que pusimos los{' '}
                  {estado.cloudflare.puestos} registros por ti. Solo dale a comprobar.
                </p>
              </div>
            ) : (
              <p className="text-[11px] text-muted-foreground">
                Captura estos {estado.registros.length} renglones donde vive tu dominio.
                Cópialos tal cual, sin espacios de más. Pueden tardar hasta 48 horas en surtir efecto.
              </p>
            )}
            {estado.registros.map((reg, i) => (
              <div key={i} className="p-2.5 rounded-lg bg-black/20 border border-white/5 space-y-1">
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="text-[9px] border-white/10 font-mono">{reg.tipo}</Badge>
                  <button onClick={() => copiar(reg.valor, `v${i}`)}
                    className="text-[10px] text-sky-400 hover:text-sky-300">
                    {copiado === `v${i}` ? 'copiado' : 'copiar valor'}
                  </button>
                </div>
                <p className="text-[10px] text-muted-foreground break-all">
                  <span className="text-white/70">nombre:</span> {reg.nombre}
                </p>
                <p className="text-[10px] text-muted-foreground break-all">
                  <span className="text-white/70">valor:</span> {reg.valor}
                </p>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={verificar} disabled={ocupado}
              className="border-white/10 text-xs w-full" data-testid="correo-dominio-verificar">
              {ocupado ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
              Ya los puse, comprueba si quedó
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


export default function CorreoConfig() {
  return (
    <>
      <ProveedorCorreo />
      <DominioEnvio />
    </>
  );
}
